/**
 * embed-targets — the CMS-wide shortkey embed target registry.
 *
 * A shortkey (`{{key}}`) points at ONE row in ONE module. This file is the
 * single place that says which modules are embeddable, what they are called
 * in the admin UI, where their public page lives, and which public API
 * endpoint returns their full payload.
 *
 * WHY A REGISTRY AND NOT A SWITCH EVERYWHERE
 * -------------------------------------------
 * The admin picker, the public renderer and the server all need the same
 * three facts about each module (label / page path / API path). Duplicated
 * three ways they drift, and a drift shows up as an embed that 404s in
 * production and works in the editor. One table, three consumers.
 *
 * The server keeps its own copy of the TYPE LIST (server/lib/embed-targets.ts)
 * because the two tsconfigs have separate rootDirs — the project's standing
 * precedent (see server/lib/video-urls.ts vs src/admin/lib/video-urls.ts).
 * The list is short and changes rarely; the paths here do not affect the
 * server's validation, only the client's rendering.
 */
import type { Locale } from '@/utils/localePath'

export type EmbedTargetType =
  | 'gallery'
  | 'article'
  | 'lecture'
  | 'magazine'
  | 'product_page'

export interface EmbedTargetTypeInfo {
  type: EmbedTargetType
  /** The label the admin sees. */
  label: string
  /** The public page path, WITHOUT the locale prefix. */
  pagePath(slug: string): string
  /** The public API endpoint returning this module's full payload. */
  apiPath(slug: string): string
  /**
   * True when the module carries a BODY worth inlining into another page
   * (an article or a lecture's Markdown). False for modules that are only a
   * set of photos or a link-out — those render as a card.
   */
  hasBody: boolean
}

export const EMBED_TARGET_TYPES: readonly EmbedTargetTypeInfo[] = [
  {
    type: 'gallery',
    label: '相簿',
    pagePath: (slug) => `/gallery/${slug}`,
    apiPath: (slug) => `/api/cms/public/galleries/${slug}`,
    hasBody: false,
  },
  {
    type: 'article',
    label: '文章',
    pagePath: (slug) => `/articles/${slug}`,
    apiPath: (slug) => `/api/cms/public/articles/${slug}`,
    hasBody: true,
  },
  {
    type: 'lecture',
    label: '講座',
    pagePath: (slug) => `/lectures/${slug}`,
    apiPath: (slug) => `/api/cms/public/lectures/${slug}`,
    hasBody: true,
  },
  {
    type: 'magazine',
    label: '媒體報導',
    pagePath: (slug) => `/magazine/${slug}`,
    apiPath: (slug) => `/api/cms/public/magazines/${slug}`,
    hasBody: false,
  },
  {
    type: 'product_page',
    label: '產品詳情',
    pagePath: (slug) => `/products/${slug}`,
    apiPath: (slug) => `/api/cms/public/product-pages/${slug}`,
    hasBody: false,
  },
]

/** The registry entry for one target type (throws on an unknown type). */
export function embedTargetInfo(type: EmbedTargetType): EmbedTargetTypeInfo {
  const info = EMBED_TARGET_TYPES.find((t) => t.type === type)
  if (!info) throw new Error(`unknown embed target type: ${type}`)
  return info
}

/** The public page URL of an embedded target, with the locale prefix. */
export function embedTargetHref(
  type: EmbedTargetType,
  slug: string,
  locale: Locale,
): string {
  return `/${locale}${embedTargetInfo(type).pagePath(slug)}`
}
